import { OptionGroup, Select } from '@dev-spendesk/grapes';
import { getDaysInMonth } from 'date-fns';
import { useMemo, useState } from 'react';

import { useTranslation } from 'src/core/common/hooks/useTranslation';

import {
  getInterval,
  getIntervalLabel,
  yearForCalculation,
} from './helpers/interval';

type SelectOptionValue = 'dec' | 'mar' | 'jun' | 'sep' | 'custom';

const MONTH_RANGE = {
  min: 0,
  max: 11,
};

const DAY_RANGE = {
  min: 1,
  max: 31,
};

export function SpanishDprDateSelector({
  selectedMonth,
  selectedDay,
  setSelectedMonth,
  setSelectedDay,
}: {
  selectedMonth: number | null;
  selectedDay: number | null;
  setSelectedMonth: (month: number | null) => void;
  setSelectedDay: (day: number | null) => void;
}) {
  const [selectOption, setSelectOption] = useState<SelectOptionValue>('dec');

  const { t } = useTranslation('global');
  const endFiscalYearDateOptions: {
    value: SelectOptionValue;
    label: string;
  }[] = useMemo(() => {
    return [
      {
        value: 'dec',
        label: t('bookkeep.settings.spanishDpr.dateOptions.dec31'),
      },
      {
        value: 'mar',
        label: t('bookkeep.settings.spanishDpr.dateOptions.mar31'),
      },
      {
        value: 'jun',
        label: t('bookkeep.settings.spanishDpr.dateOptions.jun30'),
      },
      {
        value: 'sep',
        label: t('bookkeep.settings.spanishDpr.dateOptions.sep30'),
      },
      {
        value: 'custom',
        label: t('bookkeep.settings.spanishDpr.dateOptions.custom'),
      },
    ];
  }, [t]);

  const monthOptionsMap = useMemo(() => {
    return [
      t('misc.months.jan'),
      t('misc.months.feb'),
      t('misc.months.mar'),
      t('misc.months.apr'),
      t('misc.months.may'),
      t('misc.months.jun'),
      t('misc.months.jul'),
      t('misc.months.aug'),
      t('misc.months.sep'),
      t('misc.months.oct'),
      t('misc.months.nov'),
      t('misc.months.dec'),
    ];
  }, [t]);
  const monthOptions = useMemo(() => {
    return monthOptionsMap
      .map((label, index) => ({
        key: index.toString(),
        label,
      }))
      .filter((option) => {
        const monthIndex = Number.parseInt(option.key);
        return monthIndex >= MONTH_RANGE.min && monthIndex <= MONTH_RANGE.max;
      });
  }, [monthOptionsMap]);

  const dateOptions = useMemo(() => {
    const maxDays = selectedMonth
      ? getDaysInMonth(new Date(yearForCalculation, selectedMonth))
      : DAY_RANGE.max;

    const actualMaxDays = Math.min(maxDays, DAY_RANGE.max);

    return [...Array(actualMaxDays).keys()]
      .map((index) => {
        const day = index + 1;
        if (day >= DAY_RANGE.min && day <= DAY_RANGE.max) {
          return {
            key: String(day),
            label: String(day),
          };
        }
        return null;
      })
      .filter(
        (option): option is { key: string; label: string } => option !== null,
      );
  }, [selectedMonth]);

  const onChangeSelectOption = (value: SelectOptionValue) => {
    setSelectOption(value);
    if (value === 'custom') {
      setSelectedMonth(null);
      setSelectedDay(null);
    } else {
      const datesMap = {
        dec: {
          month: 11,
          day: 31,
        },
        mar: {
          month: 2,
          day: 31,
        },
        jun: {
          month: 5,
          day: 30,
        },
        sep: {
          month: 8,
          day: 30,
        },
      };
      const { month, day } = datesMap[value];
      setSelectedMonth(month);
      setSelectedDay(day);
    }
  };

  return (
    <>
      <OptionGroup
        name="optionGroupField"
        options={endFiscalYearDateOptions}
        value={selectOption}
        onChange={(event) => {
          onChangeSelectOption(event.target.value as SelectOptionValue);
        }}
        className="my-16"
      />
      {selectOption === 'custom' && (
        <DateSelector
          monthOptions={monthOptions}
          dateOptions={dateOptions}
          selectedMonth={selectedMonth}
          selectedDay={selectedDay}
          setSelectedMonth={setSelectedMonth}
          setSelectedDay={setSelectedDay}
        />
      )}
      <FiscalYearSettingsLabel
        monthOptionsMap={monthOptionsMap}
        yearInterval={
          selectedMonth !== null && selectedDay !== null
            ? getInterval(selectedMonth, selectedDay)
            : null
        }
      />
    </>
  );
}

const DateSelector = ({
  monthOptions,
  dateOptions,
  selectedMonth,
  selectedDay,
  setSelectedMonth,
  setSelectedDay,
}: {
  monthOptions: { key: string; label: string }[];
  dateOptions: { key: string; label: string }[];
  selectedMonth: number | null;
  selectedDay: number | null;
  setSelectedMonth: (month: number | null) => void;
  setSelectedDay: (day: number | null) => void;
}) => {
  return (
    <div className="my-16 flex gap-8">
      <Select
        fit="parent"
        placeholder="Month"
        options={monthOptions}
        value={
          selectedMonth
            ? monthOptions.find((opt) => opt.key === String(selectedMonth))
            : undefined
        }
        onSelect={(option) => {
          setSelectedMonth(Number(option.key));
          if (selectedDay) {
            const maxDays = getDaysInMonth(
              new Date(yearForCalculation, Number(option.key)),
            );
            if (selectedDay > maxDays) {
              setSelectedDay(null);
            }
          }
        }}
      />
      <Select
        fit="parent"
        placeholder="Date"
        options={dateOptions}
        value={
          selectedDay
            ? dateOptions.find((opt) => opt.key === String(selectedDay))
            : undefined
        }
        onSelect={(option) => {
          setSelectedDay(Number(option.key));
        }}
      />
    </div>
  );
};

const FiscalYearSettingsLabel = ({
  monthOptionsMap,
  yearInterval,
}: {
  monthOptionsMap: string[];
  yearInterval: {
    start: { date: number; month: number };
    end: { date: number; month: number };
  } | null;
}) => {
  const { t } = useTranslation('global');
  if (!yearInterval) {
    return (
      <div className="rounded-8 bg-background-secondary-default body-m text-content-secondary-bg-secondary gap-8 p-16 text-left">
        <span className="text-content-secondary-bg-secondary">
          {t('bookkeep.settings.spanishDpr.dateSelector.customYearForArchival')}
        </span>
      </div>
    );
  }
  return (
    <div className="rounded-8 bg-background-secondary-default body-m text-content-secondary-bg-secondary gap-8 p-16 text-left">
      <span className="body-m text-content-secondary-bg-secondary">
        {t('bookkeep.settings.spanishDpr.dateSelector.fiscalYearForArchival')}
        <span className="title-m ml-4">
          {getIntervalLabel(
            yearInterval.start,
            yearInterval.end,
            monthOptionsMap,
          )}
        </span>
      </span>
    </div>
  );
};
